/**
 * DesktopCanvas — one container's icons: the desktop itself, or a folder in
 * its window.
 *
 * Two modes:
 *   - FREE (the desktop): icons sit where the person put them —
 *     `arrangeContainer` over the stored positions (stored first, then this
 *     session's auto-placements, then the rest in order), so loading another
 *     page never moves what is on screen. The desktop's view sets only the
 *     icon size. A Mac desktop auto-places from the top-right, down.
 *   - FLOW (a folder window): the folder's view lays everything out — sorted
 *     (folders first), flowing to fill the window and reflowing as it is
 *     resized, the way a folder window behaves in Explorer or Finder. Icon
 *     sizes flow in rows, List in columns, Details is a table with a header
 *     that sorts.
 *
 * Keyboard (one tab stop — a roving-tabindex listbox, the file dialogs'
 * model where the keys overlap). Shared:
 *   arrows                 move focus (and selection) to the nearest icon
 *   Shift+arrows           extend the selection
 *   Home / End             first / last          Ctrl/Cmd+A  select all loaded
 *   Ctrl/Cmd+X, Ctrl/Cmd+V cut here, paste into the focused container — a real
 *                          move, so no membership change needs a pointer
 *   Shift+F10 / Menu key   context menu          typing  jump to a name
 * Windows style:  Enter open · F2 rename · Delete to the Trash · Space toggle
 *                 selection · Backspace / Alt+↑ up a folder · Ctrl+arrows move
 *                 the focused icon one cell (desktop only — arrangement)
 * Mac style:      Cmd+O / Cmd+↓ open · Enter rename · Cmd+Backspace to the
 *                 Trash · Space Quick Look · Cmd+↑ enclosing folder
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  arrangeContainer,
  cellAt,
  cellIndex,
  type Cell,
  type FolderView,
  type SortKey,
} from '../../lib/desktop-layout';
import { DETAILS_HEADER_H, SORT_LABELS, metricsFor, type ViewMetrics } from '../../lib/folder-view';
import { vaultAreaAllows } from '@/lib/vault-areas';
import { useDesktop } from './desktop-context';
import {
  areaOfContainer,
  cellAtPoint,
  cellOrigin,
  columnsFor,
  cssEscape,
  desktopSlot,
  neighbourInDirection,
  rowsFor,
  type CellGeometry,
  type DesktopItem,
} from './desktop-model';
import { useContainerItems } from './useContainerItems';
import { DETAILS_COLUMNS, DesktopIcon, type IconVariant } from './DesktopIcon';
import { useElementSize } from './dom-hooks';

const TYPEAHEAD_RESET_MS = 700;
const LONG_PRESS_MS = 500;

export interface DesktopCanvasProps {
  container: string;
  /** Accessible name ("Desktop", "Files in Projects"). */
  label: string;
  className?: string;
  /** Backspace / Alt+↑ (Cmd+↑ on a Mac) — the enclosing folder. */
  onUp?: () => void;
  /** Copy for an empty container. */
  emptyMessage?: string;
  /** A folder window's view — its items flow and sort by it. Absent = the
   *  desktop itself (free positions). */
  view?: FolderView;
  /** The Details header asks to sort by a column. */
  onSort?: (sort: SortKey) => void;
  /** The window this canvas is in: opening a folder navigates it. */
  windowId?: string;
}

export function DesktopCanvas({
  container,
  label,
  className,
  onUp,
  emptyMessage,
  view,
  onSort,
  windowId,
}: DesktopCanvasProps) {
  const d = useDesktop();
  const mac = d.style === 'mac';
  const free = !view;
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const size = useElementSize(scrollRef, { width: 960, height: 560 });
  const metrics: ViewMetrics = metricsFor((view ?? d.desktopView).mode, d.style);
  const geometry: CellGeometry = useMemo(
    () => ({
      cellW: metrics.layout === 'rows' ? Math.max(200, size.width) : metrics.cellW,
      cellH: metrics.cellH,
      pad: metrics.pad,
    }),
    [metrics, size.width],
  );
  const columns = metrics.layout === 'rows' ? 1 : columnsFor(size.width, geometry);
  const rows = rowsFor(size.height, geometry);
  const data = useContainerItems({
    holderId: d.holderId,
    container,
    folders: d.folders,
    // The Trash sits on a Windows desktop; on a Mac it lives in the Dock.
    trashFull: d.trashFull,
    includeTrash: free && !mac,
    view,
  });
  const { items } = data;
  const area = areaOfContainer(container, d.foldersById);
  const acceptsFiles = d.canWrite && vaultAreaAllows(area, 'upload');

  // --- Where each icon goes ---------------------------------------------------
  const sticky = useRef(new Map<string, Cell>());
  const seenTidy = useRef(d.tidyVersion);
  if (seenTidy.current !== d.tidyVersion) {
    sticky.current = new Map();
    seenTidy.current = d.tidyVersion;
  }
  const stored = free ? d.layout.layout.containers[container] : undefined;
  const orderKey = items.map((i) => i.key).join('\n');
  // Nothing is placed until the folders, the first page of files and the
  // saved arrangement are all in — a free placement made on partial data
  // would stick for the session.
  const ready = d.ready && !data.loading;
  const positions = useMemo(() => {
    if (!ready) return new Map<string, Cell>();
    const order = orderKey ? orderKey.split('\n') : [];
    if (free) {
      return arrangeContainer(
        order,
        stored,
        columns,
        sticky.current,
        desktopSlot(d.style, columns, rows),
      );
    }
    const placed = new Map<string, Cell>();
    order.forEach((key, index) => {
      if (metrics.layout === 'rows') placed.set(key, { c: 0, r: index });
      else if (metrics.layout === 'flow')
        placed.set(key, { c: Math.floor(index / rows), r: index % rows });
      else placed.set(key, cellAt(index, columns));
    });
    return placed;
    // `orderKey` stands for `items`' order; `d.tidyVersion` resets sticky above.
  }, [ready, free, orderKey, stored, columns, rows, metrics.layout, d.style, d.tidyVersion]);
  const byKey = useMemo(() => new Map(items.map((i) => [i.key, i])), [items]);

  // Flow layouts hand the drop/paste machinery no cells: a drop into a
  // folder window auto-arranges by the view.
  const gridColumns =
    metrics.layout === 'flow' ? Math.max(1, Math.ceil(items.length / rows)) : columns;
  const { registerCanvas } = d;
  const canvasKey = windowId ?? container;
  useEffect(() => {
    registerCanvas(canvasKey, {
      container,
      positions,
      columns: gridColumns,
      items,
      loadedFiles: data.loadedFiles,
      totalFiles: data.totalFiles,
      free,
    });
  }, [
    registerCanvas,
    canvasKey,
    container,
    positions,
    gridColumns,
    items,
    data.loadedFiles,
    data.totalFiles,
    free,
  ]);
  useEffect(() => () => registerCanvas(canvasKey, null), [registerCanvas, canvasKey]);

  // Absence from a listing is proof of nothing — a file that vanished may be
  // in the Trash (its position must survive a restore) or moved elsewhere.
  // The canvas never removes a stored position because a file went missing;
  // the server's stale-cleanup sweep, which reads the tables, does that.

  // --- Selection & focus -----------------------------------------------------
  const selectedKeys = d.selection.container === container ? d.selection.keys : EMPTY;
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const [focusVisible, setFocusVisible] = useState(false);
  const activeKey = focusKey && byKey.has(focusKey) ? focusKey : (items[0]?.key ?? null);
  const anchor = useRef<string | null>(null);

  const orderIndex = useMemo(() => new Map(items.map((i, n) => [i.key, n])), [items]);
  const select = useCallback(
    (key: string, mode: 'replace' | 'toggle' | 'range') => {
      if (mode === 'range' && anchor.current && orderIndex.has(anchor.current)) {
        const a = orderIndex.get(anchor.current)!;
        const b = orderIndex.get(key) ?? a;
        const [lo, hi] = a < b ? [a, b] : [b, a];
        d.select(
          container,
          items.slice(lo, hi + 1).map((i) => i.key),
          'replace',
        );
      } else {
        d.select(container, [key], mode === 'toggle' ? 'toggle' : 'replace');
        anchor.current = key;
      }
      setFocusKey(key);
    },
    [container, d, items, orderIndex],
  );

  /** What a drag or a menu acts on: the selection when `item` is in it, else
   *  just `item` (which becomes the selection). */
  const actOn = useCallback(
    (item: DesktopItem): DesktopItem[] => {
      if (selectedKeys.has(item.key)) {
        const rest = items.filter((i) => selectedKeys.has(i.key) && i.key !== item.key);
        return [item, ...rest];
      }
      d.select(container, [item.key], 'replace');
      anchor.current = item.key;
      return [item];
    },
    [container, d, items, selectedKeys],
  );

  const renamable = useCallback(
    (item: DesktopItem) => {
      if (item.kind === 'file') return !!d.fileActions.capabilitiesOf(item.doc).rename;
      if (item.kind === 'folder') return d.canWrite && !item.folder.isProtected;
      return false;
    },
    [d],
  );

  const open = (item: DesktopItem, newWindow = false) =>
    d.openItem(item, container, { newWindow, windowId });
  const selectionOrFocused = (focused: DesktopItem) =>
    selectedKeys.size > 0 ? items.filter((i) => selectedKeys.has(i.key)) : [focused];

  // --- Keyboard --------------------------------------------------------------
  const typeahead = useRef({ text: '', at: 0 });
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (d.renaming) return;
    const mod = e.ctrlKey || e.metaKey;
    const focused = activeKey ? byKey.get(activeKey) : undefined;
    const arrows: Record<string, 'left' | 'right' | 'up' | 'down'> = {
      ArrowLeft: 'left',
      ArrowRight: 'right',
      ArrowUp: 'up',
      ArrowDown: 'down',
    };
    setFocusVisible(true);

    // Up a folder: Alt+↑ (Windows) / Cmd+↑ (Mac).
    if (e.key === 'ArrowUp' && (mac ? e.metaKey : e.altKey) && onUp) {
      e.preventDefault();
      onUp();
      return;
    }
    // Open: Cmd+↓ / Cmd+O on a Mac.
    if (mac && focused && e.metaKey && (e.key === 'ArrowDown' || e.key === 'o' || e.key === 'O')) {
      e.preventDefault();
      open(focused);
      return;
    }
    if (arrows[e.key] && focused) {
      e.preventDefault();
      const direction = arrows[e.key];
      if (mod && free && !mac) {
        // Arrangement: nudge the focused icon one cell, swapping with any
        // icon already there.
        const from = positions.get(focused.key);
        if (!from) return;
        const to = {
          c: Math.min(
            columns - 1,
            Math.max(0, from.c + (direction === 'left' ? -1 : direction === 'right' ? 1 : 0)),
          ),
          r: Math.max(0, from.r + (direction === 'up' ? -1 : direction === 'down' ? 1 : 0)),
        };
        if (to.c === from.c && to.r === from.r) return;
        const occupant = [...positions].find(
          ([key, cell]) =>
            key !== focused.key && cellIndex(cell, columns) === cellIndex(to, columns),
        );
        d.arrange(container, [
          [focused.key, to],
          ...(occupant ? ([[occupant[0], from]] as const) : []),
        ]);
        return;
      }
      const next = neighbourInDirection(positions, focused.key, direction);
      if (!next) return;
      if (e.shiftKey) {
        d.select(container, [next], 'add');
        setFocusKey(next);
      } else select(next, 'replace');
      return;
    }
    // Paste needs no focused icon — an empty folder is a fine place to paste.
    if (mod && (e.key === 'v' || e.key === 'V')) {
      e.preventDefault();
      d.paste(container);
      return;
    }
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      const target = e.key === 'Home' ? items[0] : items[items.length - 1];
      if (target) select(target.key, 'replace');
      return;
    }
    if (!focused) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      if (mac) {
        if (renamable(focused)) d.startRename(container, focused.key);
      } else open(focused, mod);
    } else if (e.key === ' ') {
      e.preventDefault();
      if (mac) d.quickLook(focused);
      else select(focused.key, 'toggle');
    } else if (e.key === 'F2' && !mac) {
      e.preventDefault();
      if (renamable(focused)) d.startRename(container, focused.key);
    } else if ((e.key === 'Delete' && !mac) || (mac && e.metaKey && e.key === 'Backspace')) {
      e.preventDefault();
      d.trash(container, selectionOrFocused(focused));
    } else if (mod && (e.key === 'x' || e.key === 'X')) {
      e.preventDefault();
      d.cut(container, selectionOrFocused(focused));
    } else if (mod && (e.key === 'a' || e.key === 'A')) {
      e.preventDefault();
      d.select(
        container,
        items.map((i) => i.key),
        'replace',
      );
    } else if ((e.key === 'F10' && e.shiftKey) || e.key === 'ContextMenu') {
      e.preventDefault();
      const el = contentRef.current?.querySelector<HTMLElement>(
        `[data-desktop-key="${cssEscape(focused.key)}"]`,
      );
      const rect = el?.getBoundingClientRect();
      d.openMenu({
        x: rect ? rect.left + rect.width / 2 : 0,
        y: rect ? rect.top + rect.height / 2 : 0,
        container,
        windowId,
        item: focused,
        returnFocus: el ?? null,
      });
    } else if (e.key === 'Backspace' && !mac && onUp) {
      e.preventDefault();
      onUp();
    } else if (e.key.length === 1 && !mod && !e.altKey && e.key !== ' ') {
      const now = Date.now();
      const text =
        now - typeahead.current.at > TYPEAHEAD_RESET_MS ? e.key : typeahead.current.text + e.key;
      typeahead.current = { text, at: now };
      const start = orderIndex.get(focused.key) ?? 0;
      const lower = text.toLowerCase();
      // A fresh letter moves past the current icon; a longer prefix may stay on it.
      const offset = text.length > 1 ? 0 : 1;
      for (let step = 0; step < items.length; step += 1) {
        const probe = items[(start + offset + step) % items.length];
        if (probe.name.toLowerCase().startsWith(lower)) {
          select(probe.key, 'replace');
          break;
        }
      }
    }
  };

  // --- How each item is drawn --------------------------------------------------
  const variant: IconVariant =
    metrics.layout === 'rows' ? 'details' : metrics.label === 'right' ? 'inline' : 'tile';
  const boxOf = (cell: Cell) => {
    const o = cellOrigin(cell, geometry);
    if (variant === 'details') return { x: 0, y: o.y, w: size.width, h: geometry.cellH };
    if (variant === 'inline')
      return { x: o.x, y: o.y, w: geometry.cellW - 6, h: geometry.cellH - 2 };
    return { x: o.x, y: o.y, w: geometry.cellW - 8, h: geometry.cellH - 6 };
  };

  // --- Marquee (mouse), long-press menu (touch), background clicks ----------
  const [marquee, setMarquee] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(
    null,
  );
  const localPoint = (clientX: number, clientY: number) => {
    const rect = contentRef.current!.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  };
  const cellUnder = (x: number, y: number) =>
    free ? cellAtPoint(x, y, columns, geometry) : undefined;
  const onBackgroundPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.target !== contentRef.current || e.button !== 0) return;
    scrollRef.current?.focus({ preventScroll: true });
    const start = localPoint(e.clientX, e.clientY);
    if (e.pointerType === 'touch') {
      const timer = setTimeout(() => {
        d.openMenu({
          x: e.clientX,
          y: e.clientY,
          container,
          windowId,
          cell: cellUnder(start.x, start.y),
        });
      }, LONG_PRESS_MS);
      const clear = () => {
        clearTimeout(timer);
        window.removeEventListener('pointerup', clear);
        window.removeEventListener('pointercancel', clear);
        window.removeEventListener('pointermove', onTouchMove);
      };
      const onTouchMove = (ev: PointerEvent) => {
        if (Math.hypot(ev.clientX - e.clientX, ev.clientY - e.clientY) > 8) clear();
      };
      window.addEventListener('pointerup', clear);
      window.addEventListener('pointercancel', clear);
      window.addEventListener('pointermove', onTouchMove);
      return;
    }
    const additive = e.ctrlKey || e.metaKey || e.shiftKey;
    const initial = additive ? new Set(selectedKeys) : new Set<string>();
    let moved = false;
    const move = (ev: PointerEvent) => {
      const p = localPoint(ev.clientX, ev.clientY);
      if (!moved && Math.hypot(p.x - start.x, p.y - start.y) < 4) return;
      moved = true;
      const box = { x0: start.x, y0: start.y, x1: p.x, y1: p.y };
      setMarquee(box);
      const left = Math.min(box.x0, box.x1);
      const right = Math.max(box.x0, box.x1);
      const top = Math.min(box.y0, box.y1);
      const bottom = Math.max(box.y0, box.y1);
      const hit = new Set(initial);
      for (const [key, cell] of positions) {
        const b = boxOf(cell);
        if (b.x < right && b.x + b.w > left && b.y < bottom && b.y + b.h > top) hit.add(key);
      }
      d.select(container, hit, 'replace');
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      setMarquee(null);
      if (!moved && !additive) d.select(container, [], 'replace');
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  // --- Files from the computer (native drag-and-drop) -----------------------
  const [fileDrop, setFileDrop] = useState<'allowed' | 'refused' | null>(null);
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = acceptsFiles ? 'copy' : 'none';
    setFileDrop(acceptsFiles ? 'allowed' : 'refused');
  };
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    setFileDrop(null);
    if (!acceptsFiles || e.dataTransfer.files.length === 0) return;
    const p = localPoint(e.clientX, e.clientY);
    d.uploadInto(container, Array.from(e.dataTransfer.files), cellUnder(p.x, p.y) ?? null);
  };

  // --- Paging ----------------------------------------------------------------
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { hasMore, loadingMore, loadMore } = data;
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !loadingMore) loadMore();
      },
      { root: scrollRef.current, rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadMore]);

  // --- Drag state for drawing -------------------------------------------------
  const session = d.drag.session;
  const draggingKeys =
    session && session.container === container ? new Set(session.items.map((i) => i.key)) : EMPTY;
  const dropCell =
    free && session?.target?.kind === 'container' && session.target.container === container
      ? session.target.cell
      : null;
  const canvasDropState =
    session?.target?.kind === 'container' &&
    session.target.container === container &&
    session.container !== container
      ? session.allowed
        ? 'allowed'
        : 'refused'
      : null;

  let maxRow = 0;
  let maxCol = 0;
  for (const cell of positions.values()) {
    maxRow = Math.max(maxRow, cell.r);
    maxCol = Math.max(maxCol, cell.c);
  }
  const horizontal = metrics.layout === 'flow';
  const contentStyle = horizontal
    ? { width: geometry.pad * 2 + (maxCol + 1) * geometry.cellW, height: '100%' }
    : metrics.layout === 'rows'
      ? { height: (items.length + 1) * geometry.cellH }
      : { height: geometry.pad * 2 + (maxRow + 2) * geometry.cellH };

  return (
    <div
      ref={scrollRef}
      className={cn(
        'relative h-full w-full outline-none',
        horizontal ? 'overflow-x-auto overflow-y-hidden' : 'overflow-y-auto overflow-x-hidden',
        className,
      )}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      onDragOver={onDragOver}
      onDragLeave={() => setFileDrop(null)}
      onDrop={onDrop}
    >
      {metrics.layout === 'rows' && view && <DetailsHeader view={view} onSort={onSort} mac={mac} />}
      <div
        ref={contentRef}
        role="listbox"
        aria-label={label}
        aria-multiselectable
        data-desktop-drop={`container:${container}`}
        data-columns={columns}
        data-cell-w={geometry.cellW}
        data-cell-h={geometry.cellH}
        data-pad={geometry.pad}
        data-top={geometry.top ?? 0}
        data-free={free ? 'true' : 'false'}
        onPointerDown={onBackgroundPointerDown}
        onContextMenu={(e) => {
          if (e.target !== contentRef.current) return;
          e.preventDefault();
          const p = localPoint(e.clientX, e.clientY);
          d.openMenu({
            x: e.clientX,
            y: e.clientY,
            container,
            windowId,
            cell: cellUnder(p.x, p.y),
            returnFocus: scrollRef.current,
          });
        }}
        className={cn(
          'relative',
          horizontal ? 'min-w-full' : 'min-h-full',
          canvasDropState === 'allowed' && 'bg-primary/5 ring-2 ring-inset ring-primary/50',
          canvasDropState === 'refused' && 'ring-2 ring-inset ring-destructive/40',
          fileDrop === 'allowed' && 'bg-primary/5 ring-2 ring-inset ring-primary/50',
          fileDrop === 'refused' && 'cursor-not-allowed ring-2 ring-inset ring-destructive/40',
        )}
        style={contentStyle}
      >
        {items.map((item) => {
          const cell = positions.get(item.key);
          if (!cell) return null;
          const hover =
            session?.target &&
            ((session.target.kind === 'folder' && session.target.folderId === item.key) ||
              (session.target.kind === 'trash' && item.kind === 'trash')) &&
            !draggingKeys.has(item.key)
              ? session.allowed
                ? 'allowed'
                : 'refused'
              : null;
          return (
            <DesktopIcon
              key={item.key}
              item={item}
              container={container}
              box={boxOf(cell)}
              variant={variant}
              metrics={metrics}
              style={d.style}
              selected={selectedKeys.has(item.key)}
              focused={focusVisible && activeKey === item.key}
              tabbable={activeKey === item.key}
              pending={d.pending.has(item.key)}
              dragging={draggingKeys.has(item.key)}
              cut={
                !!d.clipboard &&
                d.clipboard.container === container &&
                d.clipboard.keys.has(item.key)
              }
              dropHover={hover}
              renaming={d.renaming?.container === container && d.renaming.key === item.key}
              onSelect={(mode) => select(item.key, mode)}
              onOpen={(options) => open(item, !!options?.newWindow)}
              onFocus={() => setFocusKey(item.key)}
              onStartDrag={(pointer, grab) => {
                const dragged = actOn(item);
                const origin = positions.get(item.key)!;
                const offsets = new Map(
                  dragged.map((i) => {
                    const cellOf = positions.get(i.key) ?? origin;
                    return [i.key, { dc: cellOf.c - origin.c, dr: cellOf.r - origin.r }] as const;
                  }),
                );
                d.drag.begin({ container, items: dragged, offsets, grab, pointer });
              }}
              onContextMenu={(pointer, el) => {
                actOn(item);
                setFocusKey(item.key);
                d.openMenu({ ...pointer, container, windowId, item, returnFocus: el });
              }}
              onRename={(name) => d.commitRename(item, name)}
              onCancelRename={d.cancelRename}
            />
          );
        })}

        {dropCell && session && session.container === container && session.allowed && (
          <div
            aria-hidden
            className="pointer-events-none absolute rounded-md border-2 border-dashed border-primary/60"
            style={{
              left: cellOrigin(dropCell, geometry).x,
              top: cellOrigin(dropCell, geometry).y,
              width: geometry.cellW - 8,
              height: geometry.cellH - 6,
            }}
          />
        )}

        {marquee && (
          <div
            aria-hidden
            className={cn(
              'pointer-events-none absolute border',
              mac ? 'border-[#0a64d6]/70 bg-[#0a64d6]/15' : 'border-primary/60 bg-primary/10',
            )}
            style={{
              left: Math.min(marquee.x0, marquee.x1),
              top: Math.min(marquee.y0, marquee.y1),
              width: Math.abs(marquee.x1 - marquee.x0),
              height: Math.abs(marquee.y1 - marquee.y0),
            }}
          />
        )}

        {ready && items.length === 0 && (
          <p className="pointer-events-none absolute inset-x-0 top-16 text-center text-sm text-muted-foreground">
            {emptyMessage ?? 'This folder is empty.'}
          </p>
        )}
        {!ready && (
          <p className="pointer-events-none absolute inset-x-0 top-16 text-center text-sm text-muted-foreground">
            Loading…
          </p>
        )}
        <div
          ref={sentinelRef}
          className={cn(
            'absolute',
            horizontal ? 'right-0 top-0 h-full w-px' : 'bottom-0 h-px w-full',
          )}
          aria-hidden
        />
      </div>
    </div>
  );
}

/** The Details table's header — click a column to sort by it (again to flip). */
function DetailsHeader({
  view,
  onSort,
  mac,
}: {
  view: FolderView;
  onSort?: (sort: SortKey) => void;
  mac: boolean;
}) {
  const columns: SortKey[] = ['name', 'modified', 'type', 'size'];
  return (
    <div
      role="row"
      className={cn(
        'sticky top-0 z-10 grid items-center gap-3 border-b border-border px-3 text-xs font-medium text-muted-foreground',
        mac ? 'bg-card' : 'bg-muted/60',
      )}
      style={{ height: DETAILS_HEADER_H, gridTemplateColumns: DETAILS_COLUMNS }}
    >
      {columns.map((key) => {
        const active = view.sort === key;
        const Arrow = view.dir === 'asc' ? ArrowUp : ArrowDown;
        return (
          <button
            key={key}
            type="button"
            role="columnheader"
            aria-sort={active ? (view.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
            onClick={() => onSort?.(key)}
            className={cn(
              'flex min-w-0 items-center gap-1 truncate text-left hover:text-foreground',
              key === 'size' && 'justify-end',
              active && 'text-foreground',
            )}
          >
            {key === 'modified' && mac ? 'Date Modified' : SORT_LABELS[key]}
            {active && <Arrow className="h-3 w-3 shrink-0" aria-hidden />}
          </button>
        );
      })}
    </div>
  );
}

const EMPTY: ReadonlySet<string> = new Set();
